import type { PayrollRow } from '@myrewards/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FileTrigger } from 'react-aria-components';
import { documentHref, replaceDocument } from '../../api/ops';
import { SCAN } from '../../ops/labels';
import { AttachButton } from '../../ops/shared';
import { Button, ConfirmDialog, Icon, StatusPill, useToast } from '../../ui';
import { closeProblemWords } from '../labels';
import o from '../../ops/Ops.module.css';
import s from './Payroll.module.css';

const PDF = ['.pdf', 'application/pdf'];

/** Payment state never determines file usability. Each replacement is a new audited version. */
export function StatementCell({ row, onUploaded }: { row: PayrollRow; onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const toast = useToast();
  const replace = useMutation({
    mutationFn: async (next: File) => {
      if (row.statement === null) throw new Error('missing statement');
      return replaceDocument(row.award.id, row.statement.documentId, next);
    },
    onSuccess: ({ document }) => {
      onUploaded();
      toast.show({
        tone: 'done',
        title: `${document.fileName} uploaded`,
        body:
          document.scanStatus === 'pending'
            ? 'The replacement opens once the virus check clears it.'
            : 'The earning statement has been replaced.',
      });
    },
  });
  const statement = row.statement;
  if (statement === null)
    return row.canChangeStatement ? (
      <AttachButton
        requestId={row.award.id}
        purpose="earning_statement"
        lineId={row.lineId}
        label="Attach statement"
        accessibleLabel={`Attach statement for ${row.person.name}`}
        accept={PDF}
        onUploaded={onUploaded}
      />
    ) : (
      <span className={o.muted}>Not required</span>
    );
  const usable = statement.scanStatus === 'clean' || statement.scanStatus === 'not_required';
  const scan = SCAN[statement.scanStatus];
  return (
    <div className={s.statement}>
      {usable ? (
        <span className={s.attached}>
          <Icon name="check" size={14} />
          <StatusPill size="sm" tone="done" label="Attached" />
        </span>
      ) : (
        <StatusPill size="sm" tone={scan.tone} label={scan.label} />
      )}
      {usable ? (
        <a
          href={`${documentHref(row.award.id, statement.documentId)}?disposition=inline`}
          target="_blank"
          rel="noopener noreferrer"
          className={s.fileLink}
          aria-label={`View ${statement.fileName}, the earning statement for ${row.person.name}`}
        >
          {statement.fileName}
        </a>
      ) : (
        <span className={s.fileName}>{statement.fileName}</span>
      )}
      {row.canChangeStatement && (
        <FileTrigger
          acceptedFileTypes={PDF}
          onSelect={(files) => {
            replace.reset();
            setFile(files?.[0] ?? null);
          }}
        >
          <Button variant="ghost" size="sm" aria-label={`Replace statement for ${row.person.name}`}>
            Replace
          </Button>
        </FileTrigger>
      )}
      <ConfirmDialog
        isOpen={file !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFile(null);
            replace.reset();
          }
        }}
        title="Replace earning statement?"
        intro="The new file becomes the current statement after upload. Payment details and the original completion date stay the same."
        summary={[{ label: 'Statement', before: statement.fileName, value: file?.name ?? '' }]}
        withoutReason
        confirmLabel="Replace statement"
        error={replace.error === null ? null : closeProblemWords(replace.error)}
        onConfirm={async () => {
          if (file === null) return false;
          try {
            await replace.mutateAsync(file);
            return true;
          } catch {
            return false;
          }
        }}
      />
    </div>
  );
}
